import { Language } from '@core/i18n/domain/language';

/** The query param a deep link uses to pin the reader's language (`?lang=ar` · `?lang=en`). */
export const LANG_QUERY_PARAM = 'lang';

/**
 * Narrow a raw `?lang=` value to a supported {@link Language} — STRICTLY.
 *
 * Deliberately NOT `toLanguage()` (core/i18n/domain/language): that maps every
 * unknown value onto the default (ar), so `?lang=fr` would silently force Arabic on
 * a reader who asked for something we don't have. Here an unsupported / empty /
 * missing value returns `null`, which every caller reads as "leave the language
 * alone" — the same fall-through v1's root does for a junk `?lang=`.
 *
 * Case- and whitespace-tolerant (`?lang=EN`, `?lang=%20ar%20`), because these links
 * are authored by hand and by other systems.
 */
export function parseLangParam(value: unknown): Language | null {
  if (typeof value !== 'string') return null;
  const lang = value.trim().toLowerCase();
  return lang === 'ar' ? Language.Ar : lang === 'en' ? Language.En : null;
}

/**
 * Read `?lang=` off a RAW query string (`location.search`) — for callers that run
 * before the router has parsed the url (e.g. the root component's constructor, which
 * is the only place early enough to seed the language before the lazy /v2 subtree
 * boots and reads it). Pure + SSR-safe: string work only.
 *
 * A REPEATED param (`?lang=ar&lang=en`) is rejected rather than resolved to one of the
 * values: the link is ambiguous about what it wants, and Angular hands a repeated param
 * to its own consumers as an ARRAY (which {@link parseLangParam} also rejects), so
 * picking the first here would make the two readers disagree about the same url.
 */
export function readLangQueryParam(search: string | null | undefined): Language | null {
  if (!search) return null;
  return parseLangParam(singleLangValue(search));
}

/**
 * Would V1's own `?lang=` handler accept this query string? v1 compares the parsed
 * param with `lang === 'ar' || lang === 'en'` — EXACT, so it rejects the variants
 * {@link parseLangParam} tolerates (`EN`, ` ar `).
 *
 * Exists so a v1-rendered visit is never half-switched: on a v1 url only v1's handler
 * can switch v1 (it reloads the page, which is what makes the switch consistent
 * there), so seeding v2 from a value v1 rejects would leave the two i18n systems in
 * different languages with nothing in the url left to explain why.
 */
export function v1AcceptsLangParam(search: string | null | undefined): boolean {
  if (!search) return false;
  const raw = singleLangValue(search);
  return raw === Language.Ar || raw === Language.En;
}

/**
 * The `?lang=` value when the param appears EXACTLY ONCE, else `null`.
 *
 * The single-occurrence rule is what keeps this file's acceptance set identical to v1's:
 * Angular's url parser collapses a repeated key into an array, so v1's
 * `lang === 'ar' || lang === 'en'` test rejects `?lang=ar&lang=en` — whereas
 * `URLSearchParams.get` would quietly return `'ar'` and desync the two systems.
 */
function singleLangValue(search: string): string | null {
  const values = new URLSearchParams(search).getAll(LANG_QUERY_PARAM);
  return values.length === 1 ? values[0]! : null;
}
